import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'

const SLIDE_OUT_DURATION = 50
const SLIDE_IN_DURATION = 100
const SHIMMER_DURATION = 3000

const useStyles = makeStyles(() => ({
  textContainer: {
    position: 'relative',
    minHeight: '20px',
    maxHeight: '40px',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  shimmerText: {
    background: 'linear-gradient(90deg, #9ca3af 25%, #374151 50%, #9ca3af 75%)',
    backgroundSize: '200% 100%',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '18px',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    maxHeight: '36px',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  slideInUp: {
    animation: `$slideInUp ${SLIDE_IN_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both, $shimmer ${SHIMMER_DURATION}ms ease-in-out infinite`,
  },
  slideOutUp: {
    animation: `$slideOutUp ${SLIDE_OUT_DURATION}ms cubic-bezier(0.55, 0.055, 0.675, 0.19) both, $shimmer ${SHIMMER_DURATION}ms ease-in-out infinite`,
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  '@keyframes slideInUp': {
    '0%': { transform: 'translateY(8px)', opacity: 0 },
    '25%': { opacity: 0 },
    '100%': { transform: 'translateY(0)', opacity: 1 },
  },
  '@keyframes slideOutUp': {
    '0%': { transform: 'translateY(0)', opacity: 1 },
    '100%': { transform: 'translateY(-8px)', opacity: 0 },
  },
}))

type AdaShimmerTextProps = {
  text: string
  /** When false, update caption in place (for live speech). Default true for status lines. */
  animateChanges?: boolean
}

const AdaShimmerText: React.FC<AdaShimmerTextProps> = ({ text, animateChanges = true }) => {
  const classes = useStyles()
  const [currentContent, setCurrentContent] = useState(text)
  const [isSlideOut, setIsSlideOut] = useState(false)

  const processedContent = (animateChanges ? currentContent || text : text).replace(/\n+/g, ' ').trim()

  useEffect(() => {
    if (!animateChanges) {
      return
    }
    if (text !== currentContent) {
      setIsSlideOut(true)
      const timer = setTimeout(() => {
        setCurrentContent(text)
        setIsSlideOut(false)
      }, SLIDE_OUT_DURATION)
      return () => clearTimeout(timer)
    }
  }, [animateChanges, text, currentContent])

  if (!processedContent) {
    return null
  }

  const animationClass = animateChanges
    ? isSlideOut
      ? classes.slideOutUp
      : classes.slideInUp
    : classes.slideInUp

  return (
    <div className={classes.textContainer}>
      <div className={`${classes.shimmerText} ${animationClass}`}>{processedContent}</div>
    </div>
  )
}

export default AdaShimmerText
